// Configuration for page titles and metadata
export const PAGE_TITLES = {
  // Auth pages
  LOGIN: 'Login',
  SET_PASSWORD: 'Set Password',
  
  // Main pages
  DASHBOARD: 'Dashboard',
  ROOFTOP_DASHBOARD: 'Rooftop Dashboard',
  ROOFTOP_REPORTS: 'Rooftop Reports',
  ROOFTOP_INVERTERS: 'Rooftop Inverters',
  CLIENT_SELECTION: 'Client Selection',
  SELECT_ORGANIZATION: 'Select Organization',
  PROJECT_TYPE_SELECTION: 'Project Type Selection',
  PROJECT_SELECTION: 'Project Selection',
  PARTY_SELECTION: 'Client Selection',
  STAGE_SELECTION: 'Stage Selection',
  DOC_LIBRARY: 'Document Library',
  PROJECT_DOC_LIBRARY: 'Project Document Library',
  PROCESS_TEMPLATE_STAGE_SELECTION: 'Process Template Stage Selection',
  
  // Admin pages
  ADMIN_DASHBOARD: 'Admin Dashboard',
  ADMIN_CLIENTS: 'Client Management',
  ADMIN_PROJECTS: 'Project Management',
  ADMIN_PARTY: 'Client Configuration',
  ADMIN_PERMISSIONS: 'Permission Management',
  ADMIN_ROLES: 'Role Management',
  ADMIN_USERS: 'User Management',
  ADMIN_EMPLOYEES: 'Employee Management',
  ADMIN_EMPLOYEES_PROJECTS: 'Employee Projects Management',
  ADMIN_EMPLOYEES_PERMISSIONS: 'Employee Permissions Management',
  
  // Client pages
  PLANT_DETAILS: 'Plant Details',
  PLANT_PERFORMANCE: 'Plant Performance',
  INVERTERS: 'Inverters',
  ABT_METERS: 'ABT Meters',
  REPORTS: 'Reports',
  SLDC: 'SLDC',
  DOCUMENTS: 'Documents',
  DAILY_GENERATION: 'Daily Generation',
  LIVE_DATA: 'Live Data',
  SPARE_LIST: 'Spare List',
  MODULE_CLEANING: 'Module Cleaning',
  CHECKLIST: 'Checklist',
  EXPENSE_CODE: 'Expense Code',
  INCOME_CODE: 'Income Code',
  PROJECT_CODE: 'Project Code',
  PARTY_CODE: 'Party Code',
  PARTY_CODE_CONFIG: 'Party Code Configuration',
  
  // Organization pages
  ORGANIZATION_DASHBOARD: 'Organization Dashboard',
  ORGANIZATION_PROJECTS: 'Projects',
  ORGANIZATION_SETTINGS: 'Organization Settings',
  ORGANIZATION_PROJECT_TYPE_LISTING: 'Project Type Listing',

  // Design Stage Pages
  DESIGN_DOCUMENTS: 'Design Documents',
  DESIGN_APPROVED_DOCS: 'Approved Documents',
  DESIGN_UPLOADED_DOCS: 'Uploaded Documents',
  DESIGN_BOM: 'Design Bill of Material',
  DESIGN_INPUT_FORM: 'Design Input Form',

  // Purchase Stage Pages
  PURCHASE_DESIGN_DOCUMENTS: 'Purchase Design Documents',
  PURCHASE_INPUT_FORM: 'Purchase Input Form',

  // Land Stage Pages 
  LAND_DASHBOARD: 'Dashboard',
  LAND_DOCUMENTS: 'Documents',
  LAND_QUESTIONNAIRE: 'Land Questionnaire',
  LAND_INPUT_FORM: 'Land Input Form',
  
  // Statutory Stage Pages
  STATUTORY_PROCESSES: 'Processes',
  STATUTORY_QUESTIONNAIRE: 'Statutory Questionnaire',
  
  // Execution Stage Pages
  EXECUTION_DPR: 'Execution DPR',
  EXECUTION_DPR_CONFIG: 'DPR Configuration',
  
  // Error pages
  NOT_FOUND: 'Page Not Found',
  ACCESS_DENIED: 'Access Denied',

  // Contact pages
  CONTACT_DETAILS: 'Contact Details',
  
  // Settings
  SETTINGS: 'Settings',
  PROFILE: 'Profile',
  
  // Project Finance pages
  PROJECT_FINANCE_DASHBOARD: 'Finance Dashboard',
  PROJECT_FINANCE_PANDL: 'P & L Statement',
  PROJECT_FINANCE_GST: 'GST Statement',
  PROJECT_FINANCE_PO: 'PO Statement',
  PROJECT_FINANCE_PAYMENT: 'Payment Statement',

  // Super Admin pages
  TELEMETRY_DASHBOARD: 'Telemetry Dashboard',
  SUPERADMIN_LIVE_FEED: 'Live Feed Config',
  SUPERADMIN_SLDC_DATA: 'SLDC Data Admin'
} as const;

export const DEFAULT_TITLE_POSTFIX = ' - Climate Detox Renewables (CDRPL)' as const;

export const DEFAULT_LAND_STAGE_TITLE = '- Land Stage' as const;
export const DEFAULT_STATUTORY_STAGE_TITLE = '- Statutory Stage' as const;
export const DEFAULT_DESIGN_STAGE_TITLE = '- Design Stage' as const;
export const DEFAULT_PURCHASE_STAGE_TITLE = '- Purchase Stage' as const;

export const PAGE_DESCRIPTIONS = {
  [PAGE_TITLES.LOGIN]: 'Login to access your CDRPL dashboard',
  [PAGE_TITLES.DASHBOARD]: 'Main dashboard with overview of your energy generation data',
  [PAGE_TITLES.ROOFTOP_DASHBOARD]: 'Rooftop generation analytics and insights',
  [PAGE_TITLES.ROOFTOP_INVERTERS]: 'Daily inverter-wise energy generation and performance metrics',
  [PAGE_TITLES.CLIENT_SELECTION]: 'Select a client to view their energy generation reports and data',
  [PAGE_TITLES.PROJECT_SELECTION]: 'Select a project to access its data and management features',
  [PAGE_TITLES.PLANT_DETAILS]: 'Detailed information about the selected plant',
  [PAGE_TITLES.PLANT_PERFORMANCE]: 'Performance metrics and analytics for the plant',
  [PAGE_TITLES.INVERTERS]: 'Inverter status and performance data',
  [PAGE_TITLES.ABT_METERS]: 'ABT meter readings and analytics',
  [PAGE_TITLES.REPORTS]: 'Generate and view energy reports',
  [PAGE_TITLES.ROOFTOP_REPORTS]: 'Advanced reporting for rooftop generation data',
  [PAGE_TITLES.DOCUMENTS]: 'Document management and file uploads',
  [PAGE_TITLES.LAND_QUESTIONNAIRE]: 'Track questions and answers for land acquisition clarifications',
  [PAGE_TITLES.STATUTORY_QUESTIONNAIRE]: 'Track statutory compliance queries and responses for the selected stage',
  [PAGE_TITLES.EXECUTION_DPR]: 'Daily Progress Report for tracking execution work progress',
  [PAGE_TITLES.EXECUTION_DPR_CONFIG]: 'Configure DPR work categories, items, and settings',
  [PAGE_TITLES.DAILY_GENERATION]: 'Daily energy generation reports',
  [PAGE_TITLES.SPARE_LIST]: 'Manage spare parts inventory and stock',
  [PAGE_TITLES.ADMIN_DASHBOARD]: 'Administrative dashboard for system management',
  [PAGE_TITLES.ORGANIZATION_DASHBOARD]: 'Organization overview and management',
  [PAGE_TITLES.NOT_FOUND]: 'The requested page could not be found',
  [PAGE_TITLES.ACCESS_DENIED]: 'You do not have permission to access this page',
  [PAGE_TITLES.CONTACT_DETAILS]: 'Contact details for site and IT incharges',
  [PAGE_TITLES.PROCESS_TEMPLATE_STAGE_SELECTION]: 'Select a process template stage to manage',
} as const;

export const getPageTitle = (title: string): string => {
  return title.includes('CDRPL') ? title : `${title} - CDRPL`;
};

export const getPageDescription = (title: string): string => {
  return PAGE_DESCRIPTIONS[title as keyof typeof PAGE_DESCRIPTIONS] || 'CDRPL - Daily Generation Report';
};

export default PAGE_TITLES;
